import type React from 'react';
import type { FC } from 'react';

import classNames from 'classnames';

import { Icon } from '@iconik-io/uad';

import { Link } from '../../../components/link/link';
import { ICONS } from '../../../icons/icon';
import { ui } from '../../../utils/helpers';
import PopoverTrigger from '../../popover_trigger/popover_trigger';
import ActionIcon from './action_icon/action_icon';
import type { MenuItem } from './types';

import mixins from '../../../styles/core/mixins.module.css';
import styles from './menu.module.css';

type Props = {
  menuItems?: MenuItem[];
  onMenuItemClick?: (menuItem: MenuItem, event: React.MouseEvent) => void;
  onAdditionalMenuItemClick?: (
    menuItem: MenuItem,
    event: React.MouseEvent,
  ) => void;
  showIcons?: boolean;
  dark?: boolean;
  contextItemsAmount?: number;
  helpMessage?: string;
  useFixedWidthForSubItems?: boolean;
};

const Menu: FC<Props> = (props) => {
  const {
    menuItems = [],
    onMenuItemClick,
    onAdditionalMenuItemClick,
    showIcons = false,
    dark,
    helpMessage,
    useFixedWidthForSubItems,
  } = props;

  const actionsWithIcons = menuItems.some((menuItem) => menuItem.icon);

  return (
    <div
      className={classNames(styles.root, {
        [styles.dark]: dark,
        [styles.root_max_width]: useFixedWidthForSubItems,
      })}
      role="menu"
    >
      {menuItems.map((item, index) => {
        const menuItem: MenuItem = item ?? { label: '' };
        if (menuItem.separator) {
          return <div className={styles.separator} key={index} />;
        }

        const actionIcon = menuItem.checked ? ICONS.Tick : menuItem.icon;

        const subitemsCount = menuItem.subitems?.length ?? 0;
        let content;
        const { contextItemsAmount } = props;
        if (subitemsCount > 0) {
          content = (
            <PopoverTrigger
              positionToTrigger="overlap"
              triggerOn="hover"
              triggerElement={
                <div
                  className={classNames(styles.item_content, {
                    [styles.disabled]: menuItem.disabled,
                  })}
                >
                  <div className={styles.item_icon_label}>
                    <ActionIcon icon={actionIcon} label={menuItem.label} />
                    {menuItem.label}
                  </div>
                  <Icon
                    name="chevron_small_isolated_e"
                    className={styles.item_expand_icon}
                  />
                </div>
              }
              usePortal={false}
            >
              <Menu
                menuItems={menuItem.subitems}
                onMenuItemClick={onMenuItemClick}
                onAdditionalMenuItemClick={onAdditionalMenuItemClick}
                showIcons={showIcons}
                contextItemsAmount={contextItemsAmount}
                dark={dark}
                useFixedWidthForSubItems={useFixedWidthForSubItems}
              />
            </PopoverTrigger>
          );
        } else {
          content = (
            <div className={styles.item_icon_label}>
              <ActionIcon icon={actionIcon} label={menuItem.label} />
              <div
                className={
                  useFixedWidthForSubItems ? styles.ellipsis : undefined
                }
              >
                {menuItem.label}
                {menuItem.helpMessage ? (
                  <div className={styles.help_message}>
                    {menuItem.helpMessage}
                  </div>
                ) : null}
              </div>
              <div>
                {menuItem.amount ||
                (contextItemsAmount && contextItemsAmount > 1) ? (
                  <>&nbsp;({menuItem.amount ?? contextItemsAmount})</>
                ) : (
                  ''
                )}
              </div>
            </div>
          );
        }

        const itemElement =
          menuItem.link && !menuItem.disabled ? (
            <Link
              role={menuItem.checkable ? 'menuitemcheckbox' : 'menuitem'}
              aria-label={menuItem.label}
              aria-checked={menuItem.checked ? 'true' : 'false'}
              aria-disabled={menuItem.disabled}
              className={classNames(styles.item, mixins.reset_button, {
                [styles.disabled]: menuItem.disabled,
                [styles.icon_indent]:
                  (!actionIcon && actionsWithIcons) ||
                  (menuItem.checkable && !menuItem.checked),
              })}
              to={menuItem.link}
              target={menuItem.target}
              onClick={(event) => {
                if (menuItem.disabled || subitemsCount > 0) {
                  return;
                }
                onMenuItemClick?.(menuItem, event);
              }}
            >
              {content}
            </Link>
          ) : (
            <div
              role={menuItem.checkable ? 'menuitemcheckbox' : 'menuitem'}
              aria-label={menuItem.label}
              aria-checked={menuItem.checked ? 'true' : 'false'}
              aria-disabled={menuItem.disabled}
              className={classNames(styles.item, mixins.reset_button, {
                [styles.disabled]: menuItem.disabled,
                [styles.icon_indent]:
                  (!actionIcon && actionsWithIcons) ||
                  (menuItem.checkable && !menuItem.checked),
                [styles.ellipsis]: useFixedWidthForSubItems,
              })}
              onClick={(event) => {
                if (menuItem.disabled || subitemsCount > 0) {
                  return;
                }
                onMenuItemClick?.(menuItem, event);
              }}
              {...ui({ action: menuItem.dataUi ?? menuItem.name })}
            >
              {content}
            </div>
          );

        const additionalElement = menuItem.additionalMenuItem ? (
          <button
            type="button"
            disabled={menuItem.disabled}
            className={classNames(
              styles.additional_action,
              mixins.reset_button,
            )}
            onClick={(event) => {
              if (!menuItem.disabled && onAdditionalMenuItemClick) {
                onAdditionalMenuItemClick(menuItem, event);
              }
            }}
          >
            {menuItem.additionalMenuItem.iconName && (
              <Icon name={menuItem.additionalMenuItem.iconName} size="xs" />
            )}
          </button>
        ) : null;

        return (
          <div className={styles.item_wrapper} key={index}>
            {itemElement}
            {additionalElement}
          </div>
        );
      })}

      {helpMessage ? (
        <div className={styles.bottom_help_message}>{helpMessage}</div>
      ) : null}
    </div>
  );
};

export default Menu;
